import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider } from './i18n/LanguageContext';
import './index.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';
import DocumentsPage from './pages/DocumentsPage';
import SharedDocumentsPage from './pages/SharedDocumentsPage';
import UsersPage from './pages/UsersPage';
import InvitesPage from './pages/InvitesPage';
import AuditLogsPage from './pages/AuditLogsPage';
import SettingsPage from './pages/SettingsPage';
import AuthCallback from './pages/AuthCallback';
import CalendarPage from './pages/CalendarPage';
import TasksPage from './pages/TasksPage';
import PendingUsersPage from './pages/PendingUsersPage';
import RegistrationDocsPage from './pages/RegistrationDocsPage';
import TemplatesPage from './pages/TemplatesPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Branding Context
const BrandingContext = createContext(null);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    return { appName: 'SecureVault', logoUrl: null, theme: {} };
  }
  return context;
};

// Convert hex color to HSL string for CSS variables
function hexToHSL(hex) {
  if (!hex || !hex.startsWith('#')) return null;
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyThemeToDOM(branding) {
  const root = document.documentElement;
  const colors = branding.colors || {};

  // Map theme colors to Shadcn CSS variables (HSL)
  if (colors.primary) {
    const hsl = hexToHSL(colors.primary);
    if (hsl) {
      root.style.setProperty('--primary', hsl);
      root.style.setProperty('--ring', hsl);
      root.style.setProperty('--chart-1', hsl);
    }
  }
  if (colors.secondary) {
    const hsl = hexToHSL(colors.secondary);
    if (hsl) {
      root.style.setProperty('--secondary', hsl);
      root.style.setProperty('--chart-2', hsl);
    }
  }
  if (colors.background) {
    const hsl = hexToHSL(colors.background);
    if (hsl) {
      root.style.setProperty('--background', hsl);
    }
  }
  if (colors.surface) {
    const hsl = hexToHSL(colors.surface);
    if (hsl) {
      root.style.setProperty('--card', hsl);
      root.style.setProperty('--popover', hsl);
      root.style.setProperty('--muted', hsl);
    }
  }
  if (colors.border) {
    const hsl = hexToHSL(colors.border);
    if (hsl) {
      root.style.setProperty('--border', hsl);
      root.style.setProperty('--input', hsl);
    }
  }
  if (colors.error) {
    const hsl = hexToHSL(colors.error);
    if (hsl) root.style.setProperty('--destructive', hsl);
  }

  // Set raw hex values as custom properties for direct use
  Object.entries(colors).forEach(([key, value]) => {
    if (value) root.style.setProperty(`--sv-${key.replace(/_/g, '-')}`, value);
  });

  // Typography
  if (branding.font_family) {
    root.style.setProperty('--sv-font-body', `'${branding.font_family}', sans-serif`);
    document.body.style.fontFamily = `'${branding.font_family}', sans-serif`;
  }
  if (branding.heading_font) {
    root.style.setProperty('--sv-font-heading', `'${branding.heading_font}', sans-serif`);
  }

  // Button style
  if (branding.button_style) {
    const radiusMap = { default: '0.25rem', rounded: '0.5rem', pill: '9999px', square: '0' };
    root.style.setProperty('--radius', radiusMap[branding.button_style] || '0.25rem');
  }
}

// Device Layout Context
const DeviceLayoutContext = createContext(null);

export const useDeviceLayout = () => {
  const context = useContext(DeviceLayoutContext);
  if (!context) {
    return { config: {}, isMobile: false, loaded: false };
  }
  return context;
};

const DEVICE_LAYOUT_DEFAULTS = {
  desktop: {
    sidebar_visible: true, sidebar_collapsed: false, dashboard_columns: 4,
    card_style: 'default', show_header_search: true, show_quick_actions: true,
    show_recent_activity: true, show_stats_bar: true, content_max_width: '1400px'
  },
  mobile: {
    sidebar_visible: false, bottom_nav: true, dashboard_columns: 1,
    card_style: 'compact', show_header_search: false, show_quick_actions: true,
    show_recent_activity: false, show_stats_bar: true, content_max_width: '100%'
  }
};

export const DeviceLayoutProvider = ({ children }) => {
  const [layout, setLayout] = useState(DEVICE_LAYOUT_DEFAULTS);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchLayout = async () => {
      try {
        const res = await fetch(`${API_URL}/api/branding/device-layout`);
        if (res.ok) {
          const data = await res.json();
          setLayout({
            desktop: { ...DEVICE_LAYOUT_DEFAULTS.desktop, ...data.desktop },
            mobile: { ...DEVICE_LAYOUT_DEFAULTS.mobile, ...data.mobile }
          });
        }
      } catch { /* use defaults */ }
      setLoaded(true);
    };
    fetchLayout();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const config = isMobile ? layout.mobile : layout.desktop;

  const refreshLayout = async () => {
    try {
      const res = await fetch(`${API_URL}/api/branding/device-layout`);
      if (res.ok) {
        const data = await res.json();
        setLayout({
          desktop: { ...DEVICE_LAYOUT_DEFAULTS.desktop, ...data.desktop },
          mobile: { ...DEVICE_LAYOUT_DEFAULTS.mobile, ...data.mobile }
        });
      }
    } catch { /* silent */ }
  };

  return (
    <DeviceLayoutContext.Provider value={{ config, isMobile, loaded, layout, refreshLayout }}>
      {children}
    </DeviceLayoutContext.Provider>
  );
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({
    appName: 'SecureVault',
    logoUrl: null,
    loginLogoUrl: null,
    backgroundLogoUrl: null,
    backgroundLogoOpacity: 0.05,
    backgroundLogoSize: '400px',
    backgroundLogoPosition: 'center',
    colors: {},
    buttonStyle: 'default',
    buttonSize: 'medium',
    fontFamily: 'Inter',
    headingFont: 'Space Grotesk',
    availableFonts: [],
    customText: {},
    raw: {}
  });

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await fetch(`${API_URL}/api/branding`);
        if (response.ok) {
          const data = await response.json();
          setBranding({
            appName: data.app_name || 'SecureVault',
            logoUrl: data.logo_url,
            loginLogoUrl: data.login_logo_url,
            backgroundLogoUrl: data.background_logo_url,
            backgroundLogoOpacity: data.background_logo_opacity ?? 0.05,
            backgroundLogoSize: data.background_logo_size || '400px',
            backgroundLogoPosition: data.background_logo_position || 'center',
            colors: data.colors || {},
            buttonStyle: data.button_style || 'default',
            buttonSize: data.button_size || 'medium',
            fontFamily: data.font_family || 'Inter',
            headingFont: data.heading_font || 'Space Grotesk',
            availableFonts: data.available_fonts || [],
            customText: data.custom_text || {},
            raw: data
          });
          // Load Google Font if custom
          if (data.font_family && !['Inter', 'DM Sans'].includes(data.font_family)) {
            const link = document.createElement('link');
            link.href = `https://fonts.googleapis.com/css2?family=${data.font_family.replace(/ /g, '+')}:wght@300;400;500;600;700&display=swap`;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
          }
          if (data.heading_font && !['Space Grotesk'].includes(data.heading_font)) {
            const link = document.createElement('link');
            link.href = `https://fonts.googleapis.com/css2?family=${data.heading_font.replace(/ /g, '+')}:wght@300;400;500;600;700&display=swap`;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
          }
          applyThemeToDOM(data);
        }
      } catch (error) {
        console.error('Failed to fetch branding:', error);
      }
    };
    fetchBranding();
  }, []);

  const updateBranding = (newBranding) => {
    setBranding(prev => {
      const updated = { ...prev, ...newBranding };
      // Re-apply theme when branding updates
      if (newBranding.raw) {
        applyThemeToDOM(newBranding.raw);
      }
      return updated;
    });
  };

  const refreshBranding = async () => {
    try {
      const response = await fetch(`${API_URL}/api/branding`);
      if (response.ok) {
        const data = await response.json();
        const newState = {
          appName: data.app_name || 'SecureVault',
          logoUrl: data.logo_url,
          loginLogoUrl: data.login_logo_url,
          backgroundLogoUrl: data.background_logo_url,
          backgroundLogoOpacity: data.background_logo_opacity ?? 0.05,
          backgroundLogoSize: data.background_logo_size || '400px',
          backgroundLogoPosition: data.background_logo_position || 'center',
          colors: data.colors || {},
          buttonStyle: data.button_style || 'default',
          buttonSize: data.button_size || 'medium',
          fontFamily: data.font_family || 'Inter',
          headingFont: data.heading_font || 'Space Grotesk',
          availableFonts: data.available_fonts || [],
          customText: data.custom_text || {},
          raw: data
        };
        setBranding(newState);
        applyThemeToDOM(data);
      }
    } catch (error) {
      console.error('Failed to refresh branding:', error);
    }
  };

  return (
    <BrandingContext.Provider value={{ ...branding, updateBranding, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Update language preference
        if (userData.preferred_language) {
          localStorage.setItem('preferred_language', userData.preferred_language);
        }
      } else {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, otpCode = null) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, otp_code: otpCode })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }

    // Check if 2FA is required
    if (data.requires_2fa) {
      return data; // Return the 2FA requirement info
    }

    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('token', data.token);
    if (data.must_change_password) {
      setMustChangePassword(true);
    }
    if (data.user.preferred_language) {
      localStorage.setItem('preferred_language', data.user.preferred_language);
    }
    return data;
  };

  const register = async (email, password, name) => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('token', data.token);
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `import.meta.env.VITE_FRONTEND_URL/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const setUserFromOAuth = (userData) => {
    setUser(userData);
    if (userData.preferred_language) {
      localStorage.setItem('preferred_language', userData.preferred_language);
    }
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const forceChangePassword = async (newPassword) => {
    const res = await fetch(`${API_URL}/api/auth/force-change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify({ new_password: newPassword })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to change password');
    }
    setMustChangePassword(false);
    return await res.json();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      token,
      login, 
      register, 
      logout, 
      loginWithGoogle,
      setUserFromOAuth,
      checkAuth,
      updateUser,
      mustChangePassword,
      forceChangePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Force Change Password Page (shown when user logs in with temp password)
const ForceChangePasswordPage = () => {
  const { forceChangePassword, user } = useAuth();
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showPwd, setShowPwd] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return;
    if (newPassword.length < 8) return;
    setSubmitting(true);
    try {
      await forceChangePassword(newPassword);
      const { toast } = await import('sonner');
      toast.success('Password changed successfully!');
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
          </div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Change Your Password</h1>
          <p className="text-white/60 mt-2 text-sm">Your administrator has reset your password. Please set a new password to continue.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5 bg-black/40 border border-white/10 rounded-sm p-8">
          <div className="space-y-2">
            <label className="text-white/80 text-sm">New Password</label>
            <div className="relative">
              <input type={showPwd ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password" required minLength={8}
                className="w-full bg-black/50 border border-white/10 text-white h-11 px-4 rounded-sm" data-testid="force-new-password" />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 text-xs">
                {showPwd ? 'Hide' : 'Show'}
              </button>
            </div>
            <div className="space-y-0.5 text-xs">
              <p className={newPassword.length >= 8 ? 'text-green-400' : 'text-white/30'}>At least 8 characters</p>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-white/80 text-sm">Confirm Password</label>
            <input type={showPwd ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password" required
              className="w-full bg-black/50 border border-white/10 text-white h-11 px-4 rounded-sm" data-testid="force-confirm-password" />
            {confirmPassword && (
              <p className={newPassword === confirmPassword ? 'text-green-400 text-xs' : 'text-red-400 text-xs'}>
                {newPassword === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
              </p>
            )}
          </div>
          <button type="submit" disabled={submitting || newPassword.length < 8 || newPassword !== confirmPassword}
            className="w-full bg-primary text-black h-11 font-semibold rounded-sm disabled:opacity-50" data-testid="force-change-submit">
            {submitting ? 'Changing...' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { user, loading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-primary animate-pulse">
          <svg className="w-12 h-12 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force password change if needed
  if (mustChangePassword) {
    return <ForceChangePasswordPage />;
  }

  if (requiredRoles.length > 0 && !requiredRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <DocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/shared" element={
        <ProtectedRoute>
          <SharedDocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute requiredRoles={['super_admin', 'admin']}>
          <UsersPage />
        </ProtectedRoute>
      } />
      <Route path="/invites" element={
        <ProtectedRoute requiredRoles={['super_admin', 'admin']}>
          <InvitesPage />
        </ProtectedRoute>
      } />
      <Route path="/audit-logs" element={
        <ProtectedRoute requiredRoles={['super_admin', 'admin']}>
          <AuditLogsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <CalendarPage />
        </ProtectedRoute>
      } />
      <Route path="/tasks" element={
        <ProtectedRoute>
          <TasksPage />
        </ProtectedRoute>
      } />
      <Route path="/pending-users" element={
        <ProtectedRoute>
          <PendingUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/registration-documents" element={
        <ProtectedRoute requiredRoles={['super_admin', 'admin']}>
          <RegistrationDocsPage />
        </ProtectedRoute>
      } />
      <Route path="/templates" element={
        <ProtectedRoute requiredRoles={['super_admin', 'admin', 'manager']}>
          <TemplatesPage />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <BrandingProvider>
          <DeviceLayoutProvider>
            <AuthProvider>
              <AppRouter />
              <Toaster position="top-right" richColors />
            </AuthProvider>
          </DeviceLayoutProvider>
        </BrandingProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
